import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import utils from "../services/utils";
import managecookies from "../services/managecookies";
import Loading from "../comps/Loading";
import '../style/css/settingmodal.css'
import Icon from "../comps/Icon";
import GeneralManage from "../comps/GeneralManage";
import BusinessWeekScheduler from "../comps/BusinessWeekScheduler";
import Permissions from "./Permissions";
import Holidays from "../comps/Holidays";
import SeatManagement from "../comps/SeatManagement";
import BusinessIntlSettings from "../comps/BusinessIntlSettings";
import GeneralProfile from "../comps/profilepages/GeneralProfile";
import RequestsNBues from "../comps/profilepages/RequestsNBues";

// Valid section keys — a stale deep link (e.g. the removed ?section=areas /
// ?section=messageanagement) normalizes to 'general' so the body is never blank.
const VALID_SECTIONS = ['profile', 'buies', 'general', 'intl', 'setting', 'holidays', 'seatanagement', 'premissions'];
const normalizeSection = (s) => (VALID_SECTIONS.includes(s) ? s : 'general');

// Deep-link a category via ?section= (e.g. the profile button opens ?section=profile).
const initialSection = () => {
    try {
        const s = new URLSearchParams(window.location.search).get('section');
        if (s) return normalizeSection(s);
    } catch (e) { /* ignore */ }
    return 'general';
};

function SettingsModal(props) {
    const location = useLocation();
    const [userData, setUserData] = useState([])
    const [sectionView, setSectionView] = useState(initialSection)
    const [userRole, setUserRole] = useState('host')

    // Keep the section in sync with ?section= even when already on /settings (e.g. clicking
    // the profile button from the top bar while the settings page is already open).
    useEffect(() => {
        const s = new URLSearchParams(location.search).get('section');
        if (s) setSectionView(normalizeSection(s));
    }, [location.search]);

    const getBusinessName = (bus) => {
        if (bus.names && Array.isArray(bus.names) && bus.names.length > 0 && bus.names[0].name) {
            return bus.names[0].name;
        }
        if (bus.name) {
            return bus.name;
        }
        return bus.businessToken || 'Unknown Business';
    };

    const renderBusiness = () => {
        if (!userData.business || !Array.isArray(userData.business)) {
            return <option value="">No business available</option>;
        }
        return userData.business.map(bus => (
            <option key={bus.businessToken} value={bus.businessToken}>
                {getBusinessName(bus)}
            </option>
        ));
    };

    const handleChangeBus = () => {
        // Business switching logic
    };

    const renderUserInfo = () => {
        return <span>{userData.privateName} {userData.lastName}</span>
    }

    useEffect(() => {
        const data = JSON.parse(managecookies.getCookieVal('userData'));
        setUserData(data);
        // Get role from user data
        if (data && data.user && data.user.role) {
            setUserRole(data.user.role);
        }
    }, []);

    const renderSettingView = (section) => {
        switch (section) {
            case 'profile':
                return <GeneralProfile userData={userData} />
            case 'buies':
                return <RequestsNBues />
            case 'general':
                return <GeneralManage
                    renderBusiness={renderBusiness}
                    handleChangeBus={handleChangeBus}
                    isOwner={userRole === 'owner'}
                    existingBusinessData={userData.business && userData.business[0]} />
            case 'setting':
                return <BusinessWeekScheduler />
            case 'premissions':
                return <Permissions />
            case 'holidays':
                return <Holidays />
            case 'seatanagement':
                return <SeatManagement />
            case 'intl':
                return <BusinessIntlSettings />
            default:
                return null;
        }
    }

    // Grouped nav. Same sectionView keys + components; items are grouped under
    // labeled headings. `ownerOnly` items only render for owners; a group whose
    // items are all hidden drops its header too. `subtitle` feeds the page header.
    // NOTE (de-dup): 'areas' (area management) was removed — it duplicated the /live
    // "שולחנות" screen (TablesManager), which is the single source of truth for
    // areas + tables. 'messageanagement' was removed — it was a local-only mock; the
    // real messages screen is /sms (הודעות SMS in the main side nav).
    const navGroups = [
        {
            title: 'החשבון',
            items: [
                { key: 'profile', icon: 'customer', label: utils.useLang('EDIT_USER') || 'עריכת משתמש', subtitle: 'פרטי המשתמש והחשבון שלך' },
                { key: 'buies', icon: 'store', label: utils.useLang('MY_BUES') || 'עסקים ובקשות', subtitle: 'העסקים שלך והזמנות הצטרפות' },
            ],
        },
        {
            title: 'המסעדה',
            items: [
                { key: 'general', icon: 'info', label: utils.useLang('GENERAL'), subtitle: 'פרטי המסעדה הכלליים' },
                { key: 'setting', icon: 'clock', label: 'מועדים מיוחדים וחסימות', subtitle: 'פתיחות חריגות וחסימת תאריכים (שעות הפעילות הקבועות — במסך ניהול העסק)', ownerOnly: true },
                { key: 'holidays', icon: 'calendar', label: utils.useLang('HOLIDAYS'), subtitle: 'ניהול חגים ומועדים מיוחדים', ownerOnly: true },
                { key: 'intl', icon: 'store', label: 'בינלאומי', subtitle: 'מטבע, אזור זמן ושפת ברירת מחדל', ownerOnly: true },
            ],
        },
        {
            title: 'הזמנות',
            items: [
                { key: 'seatanagement', icon: 'utensils', label: utils.useLang('SEAT_MANAGEMENT'), subtitle: 'הגדרות וכללים לניהול הזמנות', ownerOnly: true },
            ],
        },
        {
            title: 'צוות',
            items: [
                { key: 'premissions', icon: 'users', label: utils.useLang('PERMISSIONS'), subtitle: 'ניהול הרשאות אנשי הצוות', ownerOnly: true },
            ],
        },
    ];

    if (!userData || !userData.business) {
        return <Loading />;
    }

    const isOwner = userRole === 'owner';
    const visibleGroups = navGroups
        .map(g => ({ ...g, items: g.items.filter(it => !it.ownerOnly || isOwner) }))
        .filter(g => g.items.length > 0);

    // Role-aware effective section: if the current section isn't VISIBLE for this role
    // (e.g. a non-owner deep-links ?section=premissions, or an owner-only section), fall
    // back to 'general' so we never render an owner-only panel to a non-owner. This gates
    // BOTH the header (activeItem) and the rendered body (renderSettingView).
    const visibleKeys = visibleGroups.flatMap(g => g.items).map(it => it.key);
    const effectiveSection = visibleKeys.includes(sectionView) ? sectionView : 'general';
    const activeItem = visibleGroups
        .flatMap(g => g.items)
        .find(it => it.key === effectiveSection);

    return (
        <div className={'setttings-container-modal'}>
            <section className="side-menu" aria-label="הגדרות">
                <nav className="menu-list">
                    {visibleGroups.map(group => (
                        <div className="menu-group" key={group.title}>
                            <div className="menu-group-title">{group.title}</div>
                            <ul className="menu-group-items">
                                {group.items.map(item => (
                                    <li key={item.key}>
                                        <button type="button"
                                            className={`menu-item ${effectiveSection === item.key ? 'active' : ''}`}
                                            aria-current={effectiveSection === item.key ? 'page' : undefined}
                                            onClick={() => setSectionView(item.key)}>
                                            {item.icon && <Icon className="menu-item-icon" name={item.icon} size={19} />}
                                            <span className="menu-item-label">{item.label}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>
            </section>
            <div className={'area-full-container'}>
                {activeItem && (
                    <header className="settings-page-header">
                        <h2 className="settings-page-title">{activeItem.label}</h2>
                        {activeItem.subtitle && (
                            <p className="settings-page-subtitle">{activeItem.subtitle}</p>
                        )}
                    </header>
                )}
                {renderSettingView(effectiveSection)}
            </div>
        </div>
    );
}

export default SettingsModal;